/**
 * Self-service account settings — change display name, email, and password.
 * Available to every authenticated dashboard user. Linked from the top-nav
 * user menu.
 */
import { useState } from 'react';
import { useAppAuth } from '@/contexts/AppAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { User, Mail, KeyRound } from 'lucide-react';
import { appApi, formatApiErrorDetail } from '@/lib/appApi';

export default function AccountSettingsPage() {
  const { user, refresh } = useAppAuth();
  const [displayName, setDisplayName] = useState(user?.display_name || user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [busy, setBusy] = useState(false);

  const emailChanged = email.trim().toLowerCase() !== (user?.email || '').toLowerCase();
  const wantsPasswordChange = !!newPw || !!confirmPw;

  async function save(e) {
    e.preventDefault();
    if (wantsPasswordChange) {
      if (newPw.length < 8) { toast.error('New password must be at least 8 characters'); return; }
      if (newPw !== confirmPw) { toast.error("New passwords don't match"); return; }
    }
    if ((emailChanged || wantsPasswordChange) && !currentPw) {
      toast.error('Current password is required to change email or password');
      return;
    }
    const payload = {};
    if (displayName.trim() && displayName.trim() !== (user?.display_name || user?.name || '')) {
      payload.display_name = displayName.trim();
    }
    if (emailChanged) payload.email = email.trim();
    if (wantsPasswordChange) payload.new_password = newPw;
    if (currentPw) payload.current_password = currentPw;
    if (Object.keys(payload).length === 0) { toast.info('Nothing to save'); return; }

    setBusy(true);
    try {
      await appApi.put('/me/profile', payload);
      toast.success('Account updated');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || 'Could not update account');
    } finally { setBusy(false); }
  }

  return (
    <div className="max-w-2xl space-y-6" data-testid="account-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Account settings</h1>
        <p className="text-sm text-gray-500 mt-0.5">Update your display name, email, and password. Changes persist across redeploys.</p>
      </div>

      <form onSubmit={save} className="space-y-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <User className="h-4 w-4 text-gray-400" /> Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500">Display name</Label>
              <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Your name" data-testid="account-display-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500 flex items-center gap-1.5">
                <Mail className="h-3 w-3" /> Email
              </Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="account-email" />
              {emailChanged && (
                <p className="text-xs text-amber-700 mt-1">You're changing your sign-in email. We'll ask for your current password below to confirm.</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-gray-400" /> Change password
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500">New password</Label>
              <PasswordInput
                placeholder="Leave blank to keep current"
                value={newPw} onChange={(e) => setNewPw(e.target.value)}
                autoComplete="new-password" data-testid="account-new-password"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500">Confirm new password</Label>
              <PasswordInput
                placeholder="Re-type the new password"
                value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                autoComplete="new-password" data-testid="account-confirm-password"
              />
            </div>
          </CardContent>
        </Card>

        {(emailChanged || wantsPasswordChange) && (
          <Card className="border-amber-200 bg-amber-50/40">
            <CardContent className="p-4 space-y-3">
              <div>
                <Label className="text-xs text-amber-900 font-semibold">Confirm with current password</Label>
                <p className="text-xs text-amber-800/80 mt-0.5">Required to change email or password.</p>
              </div>
              <PasswordInput
                placeholder="Current password" value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                autoComplete="current-password" data-testid="account-current-password"
              />
            </CardContent>
          </Card>
        )}

        <Separator />
        <div className="flex justify-end gap-2">
          <Button type="submit" disabled={busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="account-save">
            {busy ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>
    </div>
  );
}
